import { EyeOffIcon, GlobeIcon, LockIcon } from 'lucide-react';

import type { GroupRole, GroupVisibility } from '@/shared/api/types';
import type { IconComponent } from '@/shared/registry/types';

import type { GroupDetails, GroupListItem, IsoDate } from '../api';

export const VISIBILITIES: readonly GroupVisibility[] = ['open', 'closed', 'secret'];

export const VISIBILITY_ICON: Record<GroupVisibility, IconComponent> = {
  open: GlobeIcon,
  closed: LockIcon,
  secret: EyeOffIcon,
};

/** Sort rank: owner, moderators, members. */
export const ROLE_RANK: Record<GroupRole, number> = { owner: 0, moderator: 1, member: 2 };

export const GROUP_TABS = ['my', 'all', 'projects', 'archive'] as const;
export type GroupsTab = (typeof GROUP_TABS)[number];

export const isGroupsTab = (value: string | null | undefined): value is GroupsTab =>
  !!value && (GROUP_TABS as readonly string[]).includes(value);

/** Path of a group page (optionally a tab). */
export const groupPath = (id: string, tab?: string) =>
  tab ? `/groups/${encodeURIComponent(id)}/${encodeURIComponent(tab)}` : `/groups/${encodeURIComponent(id)}`;

/** Parses `2026-10-09` as a local date (no time zone shift, unlike `new Date(iso)`). */
export function parseIsoDate(value: IsoDate): Date {
  const [y = 1970, m = 1, d = 1] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Formats a local date as `2026-10-09`. */
export function toIsoDate(date: Date): IsoDate {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export type ProjectPhase = 'planned' | 'active' | 'overdue' | 'finished';

/**
 * Where a project stands relative to `today`: before its start (`planned`), between start and end (`active`), after its
 * end while still active (`overdue`), or archived (`finished`). `null` without dates.
 */
export function projectPhase(
  group: Pick<GroupDetails | GroupListItem, 'isProject' | 'projectStart' | 'projectEnd' | 'isArchived'>,
  today: Date = new Date(),
): ProjectPhase | null {
  if (!group.isProject) return null;
  if (group.isArchived) return 'finished';
  if (!group.projectStart && !group.projectEnd) return null;
  const day = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  if (group.projectStart && parseIsoDate(group.projectStart).getTime() > day) return 'planned';
  if (group.projectEnd && parseIsoDate(group.projectEnd).getTime() < day) return 'overdue';
  return 'active';
}

/** Share of the project's time already elapsed (0–1), or `null` without both dates. */
export function projectProgress(
  group: Pick<GroupDetails | GroupListItem, 'projectStart' | 'projectEnd'>,
  today: Date = new Date(),
): number | null {
  if (!group.projectStart || !group.projectEnd) return null;
  const start = parseIsoDate(group.projectStart).getTime();
  const end = parseIsoDate(group.projectEnd).getTime();
  if (end <= start) return today.getTime() >= end ? 1 : 0;
  return Math.min(1, Math.max(0, (today.getTime() - start) / (end - start)));
}

/** What the primary membership button of a group offers the current user. */
export type MembershipAction = 'member' | 'join' | 'request' | 'requested' | 'invited' | 'archived' | 'none';

export function membershipAction(
  group: Pick<
    GroupListItem,
    'myRole' | 'visibility' | 'isArchived' | 'hasPendingRequest' | 'hasPendingInvitation'
  >,
  /** The user joins closed groups without asking (portal administrators: they could approve their own request). */
  joinsClosedDirectly = false,
): MembershipAction {
  if (group.myRole) return 'member';
  if (group.isArchived) return 'archived';
  if (group.hasPendingInvitation) return 'invited';
  if (group.visibility === 'open') return 'join';
  if (group.visibility === 'closed') {
    if (joinsClosedDirectly) return 'join';
    return group.hasPendingRequest ? 'requested' : 'request';
  }
  return 'none';
}

/** Whether the client may subscribe to the group's realtime topic (the server rejects outsiders of secret groups). */
export const canSubscribe = (group: Pick<GroupDetails, 'visibility' | 'myRole'>) =>
  group.visibility !== 'secret' || group.myRole !== null;

/** The membership fields of a list item, derived from a group's details when needed. */
export function membershipState(
  group: GroupDetails | GroupListItem,
): Pick<
  GroupListItem,
  'myRole' | 'visibility' | 'isArchived' | 'hasPendingRequest' | 'hasPendingInvitation'
> {
  if ('permissions' in group) {
    return {
      myRole: group.myRole,
      visibility: group.visibility,
      isArchived: group.isArchived,
      hasPendingRequest: group.myJoinRequest !== null,
      hasPendingInvitation: group.myInvitation !== null,
    };
  }
  return group;
}
